import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Toolbar,
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  useMediaQuery,
  useTheme,
  Chip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import ContactFormSection from '../components/ContactFormSection';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';

const PricingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const contactFormRef = useRef(null);
  const { t, i18n } = useTranslation('pricing');
  const { t: tCommon } = useTranslation('common');
  
  // Get current language
  const currentLang = i18n.language;

  // Build pricing plans from translations
  const pricingPlans = [
    {
      id: 'personal',
      title: t('plans.personal.title'),
      price: t('plans.personal.price'),
      period: t('plans.personal.period'),
      yearlyPrice: t('plans.personal.yearlyPrice'),
      buttonText: t('plans.personal.buttonText'),
      features: t('plans.personal.features', { returnObjects: true }),
      popular: false,
      color: '#F9A825',
      action: 'contact',
      isCustomPrice: false
    },
    {
      id: 'professional',
      title: t('plans.professional.title'),
      price: t('plans.professional.price'),
      period: t('plans.professional.period'),
      yearlyPriceText: t('plans.professional.yearlyPriceText'),
      buttonText: t('plans.professional.buttonText'),
      features: t('plans.professional.features', { returnObjects: true }),
      popular: true,
      popularBadge: t('plans.professional.popularBadge'),
      color: '#00B900',
      isCustomPrice: true,
      action: 'contact'
    },
    {
      id: 'enterprise',
      title: t('plans.enterprise.title'),
      price: t('plans.enterprise.price'),
      period: t('plans.enterprise.period'),
      yearlyPriceText: t('plans.enterprise.yearlyPriceText'),
      buttonText: t('plans.enterprise.buttonText'),
      features: t('plans.enterprise.features', { returnObjects: true }),
      popular: false,
      color: '#37474F',
      isCustomPrice: true,
      action: 'contact'
    }
  ];

  // Get all plans features
  const allPlansFeatures = t('allPlansInclude.features', { returnObjects: true });
  
  // Get FAQ questions
  const faqQuestions = t('faq.questions', { returnObjects: true });

  // Dynamic SEO based on language
  const getSeoTitle = () => {
    const baseTitle = t('pageTitle');
    switch(currentLang) {
      case 'ja':
        return `${baseTitle} | LINE AIエージェント - Rebaz AI`;
      case 'en':
        return `${baseTitle} | LINE AI Agent - Rebaz AI`;
      case 'zh-CN':
        return `${baseTitle} | Line AI 代理人 - Rebaz AI`;
      default:
        return `${baseTitle} | Line AI 代理人 - Rebaz AI`;
    }
  };

  const getSeoDescription = () => {
    switch(currentLang) {
      case 'ja':
        return 'Rebaz AIは、基本版、プロフェッショナル版、エンタープライズ版など、さまざまなニーズと予算に合わせた柔軟なLINE AIエージェント料金プランを提供しています。';
      case 'en':
        return 'Rebaz AI offers flexible LINE AI Agent pricing plans including Basic, Professional, and Enterprise editions to meet your different needs and budgets.';
      case 'zh-CN':
        return 'Rebaz AI 提供多种灵活的 Line AI 代理人价格方案，基础版、专业版和企业版满足您不同的需求和预算。';
      default:
        return 'Rebaz AI 提供多種靈活的 Line AI 代理人價格方案，基礎版、專業版和企業版滿足您不同的需求和預算。';
    }
  };

  const getSeoKeywords = () => {
    switch(currentLang) {
      case 'ja':
        return 'LINE AI 価格, AIカスタマーサービスプラン, ボットカスタマーサービス費用, 自動化カスタマーサービス価格, LINEスマートカスタマーサービス';
      case 'en':
        return 'LINE AI pricing, AI customer service plans, bot customer service costs, automated customer service pricing, LINE smart customer service';
      case 'zh-CN':
        return 'Line AI 价格, AI 客服方案, 机器人客服费用, 自动化客服价格, LINE 智能客服';
      default:
        return 'Line AI 價格, AI 客服方案, 機器人客服費用, 自動化客服價格, LINE 智能客服';
    }
  };

  // OfferCatalog schema: Google accepts a single currency even on multilingual pages.
  // Use TWD pricing (primary market) and treat custom-quote plans as Offers without a fixed price.
  const offerCatalogSchema = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "name": "Rebaz AI 方案",
    "url": "https://rebaz.ai/pricing",
    "itemListElement": pricingPlans.map(plan => {
      const offer = {
        "@type": "Offer",
        "name": plan.title,
        "url": "https://rebaz.ai/pricing",
        "availability": "https://schema.org/InStock",
        "description": plan.features.join(', ')
      };
      if (!plan.isCustomPrice) {
        // 個人業務版: 1,290 TWD / 月
        const numericPrice = String(plan.price).replace(/[^0-9.]/g, '');
        offer.price = numericPrice;
        offer.priceCurrency = "TWD";
        offer.priceSpecification = {
          "@type": "UnitPriceSpecification",
          "price": numericPrice,
          "priceCurrency": "TWD",
          "billingDuration": "P1M",
          "unitText": "MONTH"
        };
      } else {
        // 企業專業版 / 企業進階版: 專人報價
        offer.priceCurrency = "TWD";
        offer.priceSpecification = {
          "@type": "PriceSpecification",
          "priceCurrency": "TWD",
          "description": plan.yearlyPriceText || "Contact sales for pricing"
        };
      }
      return offer;
    })
  };

  const pricingBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首頁", "item": "https://rebaz.ai" },
      { "@type": "ListItem", "position": 2, "name": t('pageTitle'), "item": "https://rebaz.ai/pricing" }
    ]
  };

  const handlePlanAction = (action) => {
    // Scroll to contact form
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Format price display based on language - UPDATED VERSION
  const formatPriceDisplay = (plan) => {
    if (plan.isCustomPrice) {
      return (
        <Box>
          <Typography 
            variant="h4" 
            component="div" 
            sx={{ fontWeight: 'bold', mb: 1 }}
          >
            {plan.price}
          </Typography>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ color: plan.color }}
          >
            {plan.period}
          </Typography>
        </Box>
      );
    } else {
      // Get currency from translation
      const currency = t(`plans.${plan.id}.currency`, '');
      const currencyPosition = currentLang === 'en' ? 'before' : 'after';
      
      return (
        <>
          {currencyPosition === 'before' && currency && (
            <Typography 
              variant="h4" 
              component="span" 
              sx={{ fontWeight: 'bold', mr: 0.5 }}
            >
              {currency}
            </Typography>
          )}
          <Typography 
            variant="h3" 
            component="span" 
            sx={{ fontWeight: 'bold' }}
          >
            {plan.price}
          </Typography>
          {currencyPosition === 'after' && currency && (
            <Typography 
              variant="h4" 
              component="span" 
              sx={{ fontWeight: 'bold', ml: 0.5 }}
            >
              {currency}
            </Typography>
          )}
          <Typography 
            variant="subtitle1" 
            component="span" 
            sx={{ ml: 1 }}
          >
            /{plan.period}
          </Typography>
        </>
      );
    }
  };

  // Format yearly price display - NEW FUNCTION
  const formatYearlyPrice = (plan) => {
    if (!plan.isCustomPrice) {
      const currency = t(`plans.${plan.id}.currency`, '');
      const yearlyPrice = t(`plans.${plan.id}.yearlyPrice`);
      const yearText = currentLang === 'ja' ? '年' : currentLang === 'en' ? 'year' : '年';
      const currencyPosition = currentLang === 'en' ? 'before' : 'after';
      
      return (
        <Typography 
          variant="body2" 
          sx={{ mt: 1, color: 'text.secondary', fontWeight: 'medium' }}
        >
          {currentLang === 'ja' && '年払い '}
          {currentLang === 'en' && 'Annual: '}
          {currentLang === 'zh-CN' && '年缴 '}
          {currentLang === 'zh-TW' && '年繳 '}
          {currencyPosition === 'before' && currency}
          {yearlyPrice}
          {currencyPosition === 'after' && currency && ` ${currency}`}
          /{yearText}
        </Typography>
      );
    }
    return null;
  };

  return (
    <>
      <SEO
        title={getSeoTitle()}
        description={getSeoDescription()}
        keywords={getSeoKeywords()}
        canonicalUrl="https://rebaz.ai/pricing"
        schema={offerCatalogSchema}
        breadcrumb={pricingBreadcrumb}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <PublicHeader />
        <Toolbar />

        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              mb: 2,
              pt: 8
            }}
          >
            {t('pageTitle')}
          </Typography>
          <Typography 
            variant="h5" 
            component="p" 
            color="textSecondary"
            sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            {t('pageSubtitle')}
          </Typography>
        </Box>
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1,
            py: 8,
            backgroundColor: '#f9fafc'
          }}
        >
          <Container maxWidth="lg">
            {/* Pricing Cards */}
            <Grid container spacing={4} justifyContent="center">
              {pricingPlans.map((plan, index) => (
                <Grid item xs={12} sm={12} md={4} key={index}>
                  <Card 
                    raised={plan.popular}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 2,
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)'
                      },
                      ...(plan.popular && {
                        border: `2px solid ${plan.color}`,
                        boxShadow: `0 8px 24px rgba(0,0,0,0.12)`
                      })
                    }}
                  >
                    {plan.popular && plan.popularBadge && (
                      <Chip
                        label={plan.popularBadge}
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: -12,
                          right: 24,
                          backgroundColor: plan.color,
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                      <Typography 
                        variant="h5" 
                        component="h2" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 'bold',
                          color: plan.color,
                          mb: 3 
                        }}
                      >
                        {plan.title}
                      </Typography>
                      
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                          {formatPriceDisplay(plan)}
                        </Box>
                        
                        {/* UPDATED: Use formatYearlyPrice function */}
                        {!plan.isCustomPrice && formatYearlyPrice(plan)}
                        
                        {plan.isCustomPrice && (
                          <Typography 
                            variant="body2" 
                            sx={{ mt: 2, color: 'text.secondary', fontWeight: 'medium' }}
                          >
                            {plan.yearlyPriceText}
                          </Typography>
                        )}
                      </Box>
                      
                      <List disablePadding>
                        {plan.features.map((feature, idx) => (
                          <ListItem 
                            key={idx} 
                            disableGutters
                            sx={{ py: 1 }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckIcon sx={{ color: plan.color }} />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                    
                    <CardActions sx={{ p: 4, pt: 0 }}>
                      <Button 
                        fullWidth 
                        variant={plan.popular ? "contained" : "outlined"} 
                        size="large"
                        onClick={() => handlePlanAction(plan.action)}
                        sx={{ 
                          py: 1.5,
                          borderRadius: 2,
                          ...(plan.popular ? {
                            backgroundColor: plan.color,
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' 
                                ? theme.palette.augmentColor({ color: { main: plan.color } }).dark
                                : theme.palette.augmentColor({ color: { main: plan.color } }).light,
                            }
                          } : {
                            color: plan.color,
                            borderColor: plan.color,
                            '&:hover': {
                              borderColor: plan.color,
                              backgroundColor: `${plan.color}10`,
                            }
                          })
                        }}
                      >
                        {plan.buttonText}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {/* Additional Features Section */}
            <Box sx={{ mt: 10, textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 6 }}
              >
                {t('allPlansInclude.title')}
              </Typography>
              
              <Grid container spacing={4} justifyContent="center">
                {allPlansFeatures.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        textAlign: 'center',
                        p: 3,
                        boxShadow: 1,
                        borderRadius: 2
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            {/* FAQ Section */}
            <Box sx={{ mt: 10 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 6, textAlign: 'center' }}
              >
                {t('faq.title')}
              </Typography>
              
              <Grid container spacing={4}>
                {faqQuestions.map((faq, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {faq.question}
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        {faq.answer}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            {/* Link to FAQ Page */}
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
                component={RouterLink}
                to="/faq-public"
                variant="outlined"
                color="primary"
                size="large"
                sx={{ 
                  px: 4, 
                  py: 1.2, 
                  borderRadius: 2,
                  fontWeight: 'medium'
                }}
              >
                {t('faq.viewMoreButton')}
              </Button>
            </Box>
          </Container>
        </Box>
        
        {/* Contact Form Section */}
        <Box ref={contactFormRef}>
          <ContactFormSection 
            source="pricing"
            title={t('cta.title')}
            subtitle={t('cta.subtitle')}
          />
        </Box>
        
        <Footer />
      </Box>
    </>
  );
};

export default PricingPage;